import { LookupParam, PositionLookup } from '@/types/position-lookups'
import { Position } from '@/types/positions'

export function findLookup(
  lookups: PositionLookup[],
  lookupCode: string | undefined,
) {
  return lookups.find(({ key }) => key === lookupCode)
}

export function dependentParams(
  lookup: PositionLookup,
  paramName: string,
): LookupParam[] {
  return lookup.params.filter(
    (param) =>
      param.options.kind === 'live' &&
      param.options.depends_on.includes(paramName),
  )
}

export function dependsOnValues(
  param: LookupParam,
  params: Record<string, string>,
): Record<string, string | undefined> {
  if (param.options.kind !== 'live') return {}
  return Object.fromEntries(
    param.options.depends_on.map((name) => [name, params[name] || undefined]),
  )
}

export function hasEveryRequiredParam(
  lookup: PositionLookup,
  params: Record<string, string>,
) {
  return lookup.params.every(
    (param) => !param.required || !!params[param.name]?.trim(),
  )
}

/**
 * Nothing is kept from the lookup chosen before this one, even where both
 * have a param of the same name. Two lookups can each have a `field` and mean
 * different things by it: one an employee field, the other which of two
 * people the project record names. Nothing in the catalog says whether two
 * params agree. Only the accounting system knows, and it doesn't say so until
 * the position is resolved against a real document — long after whoever set
 * it up could fix it.
 */
export function emptyParams(lookup: PositionLookup): Record<string, string> {
  return Object.fromEntries(lookup.params.map(({ name }) => [name, '']))
}

/**
 * Clears every param whose choices are fetched using the one that changed,
 * and every param downstream of those. A value read from one
 * accounting-system field means nothing under another.
 *
 * Params can be chained: C's choices can depend on B, whose choices depend on
 * A. Clearing only B would leave C holding a value that was read under the old
 * B, and C would still count as filled in.
 */
export function paramsAfterChange(
  lookup: PositionLookup,
  params: Record<string, string>,
  changedParam: string,
): Record<string, string> {
  const cleared = new Set<string>()
  const toWalk = [changedParam]

  while (toWalk.length > 0) {
    const name = toWalk.shift() as string
    dependentParams(lookup, name).forEach((param) => {
      // Also stops a catalog that somehow chains back on itself from looping
      // here forever.
      if (cleared.has(param.name)) return
      cleared.add(param.name)
      toWalk.push(param.name)
    })
  }

  return Object.fromEntries(
    lookup.params.map((param) => [
      param.name,
      cleared.has(param.name) ? '' : params[param.name] ?? '',
    ]),
  )
}

/** Reads like `Employee field: CF_LOA · Field value: Managing Principal`. */
export function summarizePositionRule(
  position: Pick<Position, 'lookup_code' | 'params'>,
  lookup: PositionLookup | undefined,
): string {
  // A position keeps working after its lookup stops being offered, so there
  // are no labels to use. Show the raw params rather than nothing.
  if (!lookup) {
    return Object.entries(position.params)
      .map(([name, value]) => `${name}: ${value}`)
      .join(' · ')
  }

  return lookup.params
    .map((param) => {
      const value = position.params[param.name]
      return value ? `${param.label}: ${value}` : null
    })
    .filter(Boolean)
    .join(' · ')
}

/**
 * Stricter than the database, which would allow both "President" and
 * "president". Two positions whose names differ only in case or spacing are
 * impossible to tell apart wherever one is picked.
 */
export function isNameTaken(name: string, takenNames: string[]): boolean {
  const trimmed = name.trim().toLowerCase()
  if (!trimmed) return false
  return takenNames.some((taken) => taken.trim().toLowerCase() === trimmed)
}
